import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Plus, Trash2, Save, X, Copy, Percent, DollarSign, Tag, Calendar } from "lucide-react";
import { API } from "./adminConfig";

const CouponsTab = () => {
  const [coupons, setCoupons] = useState([]);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couponFormData, setCouponFormData] = useState({
    code: '',
    type: 'forever_free',
    value: 100,
    duration: 'once',
    duration_in_months: '',
    usage_limit: null,
    per_user_limit: 1,
    expires_at: null,
    applies_to_plans: []
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`);
      setCoupons(response.data || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Error loading coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingCoupon && !couponFormData.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }

    try {
      if (editingCoupon) {
        // Update existing coupon
        const updateData = {
          type: couponFormData.type,
          value: parseFloat(couponFormData.value) || 0,
          duration: couponFormData.duration || 'once',
          duration_in_months: couponFormData.duration === 'repeating' ? parseInt(couponFormData.duration_in_months) || null : null,
          usage_limit: couponFormData.usage_limit ? parseInt(couponFormData.usage_limit) : null,
          times_used: couponFormData.times_used != null ? parseInt(couponFormData.times_used) : undefined,
          per_user_limit: couponFormData.per_user_limit ? parseInt(couponFormData.per_user_limit) : null,
          expires_at: couponFormData.expires_at || null,
          applies_to_plans: (couponFormData.applies_to_plans && couponFormData.applies_to_plans.length > 0) 
            ? couponFormData.applies_to_plans 
            : null
        };
        // Remove undefined values
        Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);
        
        await axios.put(`${API}/admin/coupons/${editingCoupon}`, updateData);
        toast.success('Coupon updated successfully!');
      } else {
        // Create new coupon
        const submitData = {
          ...couponFormData,
          code: couponFormData.code.toUpperCase().trim(),
          value: parseFloat(couponFormData.value) || 0,
          duration: couponFormData.duration || 'once',
          duration_in_months: couponFormData.duration === 'repeating' ? parseInt(couponFormData.duration_in_months) || null : null,
          usage_limit: couponFormData.usage_limit ? parseInt(couponFormData.usage_limit) : null,
          per_user_limit: couponFormData.per_user_limit ? parseInt(couponFormData.per_user_limit) : null,
          expires_at: couponFormData.expires_at || null,
          applies_to_plans: (couponFormData.applies_to_plans && couponFormData.applies_to_plans.length > 0) 
            ? couponFormData.applies_to_plans 
            : null
        };
        await axios.post(`${API}/admin/coupons`, submitData);
        toast.success('Coupon created successfully!');
      }
      
      fetchCoupons();
      setShowCouponForm(false);
      setEditingCoupon(null);
      setCouponFormData({
        code: '',
        type: 'forever_free',
        value: 100,
        duration: 'once',
        duration_in_months: '',
        usage_limit: null,
        per_user_limit: 1,
        expires_at: null,
        applies_to_plans: []
      });
    } catch (error) {
      console.error('Error saving coupon:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Coupon code already exists');
      } else {
        toast.error('Error saving coupon');
      }
    }
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon.code);
    setCouponFormData({
      code: coupon.code,
      type: coupon.type || 'forever_free',
      value: coupon.value || 100,
      duration: coupon.duration || 'once',
      duration_in_months: coupon.duration_in_months || '',
      usage_limit: coupon.usage_limit || '',
      times_used: coupon.times_used || 0,
      per_user_limit: coupon.per_user_limit || 1,
      expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
      applies_to_plans: coupon.applies_to_plans || []
    });
    setShowCouponForm(true);
  };

  const toggleCouponActive = async (code, currentStatus) => {
    try {
      await axios.put(`${API}/admin/coupons/${code}`, {
        active: !currentStatus
      });
      toast.success(`Coupon ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchCoupons();
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      toast.error('Error updating coupon status');
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`Are you sure you want to delete the coupon "${code}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/coupons/${code}`);
      toast.success('Coupon deleted successfully!');
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Error deleting coupon');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        <span className="ml-2 text-burgundy">Loading coupons...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Membership Coupons</h2>
        <Button 
          onClick={() => setShowCouponForm(true)}
          className="bg-burgundy hover:bg-burgundy/80 text-cream"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Coupon
        </Button>
      </div>

      {/* Coupons List */}
      <Card className="bg-white border-burgundy/20">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Active Coupons</CardTitle>
          <CardDescription>Manage ForeverFree and referral discount codes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {coupons.length === 0 ? (
              <p className="text-burgundy/60 text-center py-8">No coupons created yet</p>
            ) : (
              coupons.map(coupon => (
                <div key={coupon.id} className="border border-burgundy/20 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className="bg-burgundy text-white font-mono text-lg px-3 py-1">
                          {coupon.code}
                        </Badge>
                        <Badge variant="outline" className={`${
                          coupon.type === 'forever_free' 
                            ? 'border-emerald-500 text-emerald-600' 
                            : 'border-gold text-gold'
                        }`}>
                          {coupon.type === 'forever_free' ? 'Free Forever' : `${coupon.value}% Off First Payment`}
                        </Badge>
                        <Badge className={`${
                          coupon.active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {coupon.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="text-sm text-burgundy/70 space-y-1">
                        {coupon.type === 'percentage' && (
                          <p className="text-xs italic text-burgundy/60 mb-2">
                            {coupon.duration === 'forever' 
                              ? `Applies ${coupon.value}% discount to every payment until they cancel`
                              : coupon.duration === 'repeating'
                                ? `Applies ${coupon.value}% discount for ${coupon.duration_in_months || '?'} months, then full price`
                                : `Applies ${coupon.value}% discount to the first payment only`
                            }
                          </p>
                        )}
                        {coupon.type === 'forever_free' && (
                          <p className="text-xs italic text-emerald-600/80 mb-2">
                            💡 Grants permanent free access - no payments required
                          </p>
                        )}
                        {coupon.applies_to_plans && coupon.applies_to_plans.length > 0 && (
                          <p className="text-xs text-burgundy/70 mb-2">
                            <strong>Valid for:</strong> {coupon.applies_to_plans.map(p => p === 'monthly' ? 'Monthly' : 'Yearly').join(' & ')} membership only
                          </p>
                        )}
                        {(!coupon.applies_to_plans || coupon.applies_to_plans.length === 0) && (
                          <p className="text-xs text-burgundy/70 mb-2">
                            <strong>Valid for:</strong> All membership plans
                          </p>
                        )}
                        {coupon.usage_limit && (
                          <p><strong>Usage Limit:</strong> {coupon.times_used || 0} / {coupon.usage_limit} used</p>
                        )}
                        {!coupon.usage_limit && (
                          <p><strong>Times Used:</strong> {coupon.times_used || 0} (unlimited)</p>
                        )}
                        {coupon.per_user_limit && (
                          <p><strong>Per User Limit:</strong> {coupon.per_user_limit} uses per user</p>
                        )}
                        {coupon.expires_at && (
                          <p><strong>Expires:</strong> {new Date(coupon.expires_at).toLocaleDateString()}</p>
                        )}
                        <p><strong>Created:</strong> {new Date(coupon.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleEditCoupon(coupon)}
                        variant="outline"
                        size="sm"
                        className="border-burgundy/40 text-burgundy hover:bg-burgundy/5"
                        data-testid={`edit-coupon-${coupon.code}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => toggleCouponActive(coupon.code, coupon.active)}
                        variant="outline"
                        size="sm"
                        className={`${
                          coupon.active 
                            ? 'border-gray-400 text-gray-700 hover:bg-gray-100' 
                            : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                        }`}
                      >
                        {coupon.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        onClick={() => handleDeleteCoupon(coupon.code)}
                        variant="outline"
                        size="sm"
                        className="border-red-400 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Coupon Form Modal */}
      {showCouponForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white border-burgundy/20 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="font-serif text-burgundy">{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</CardTitle>
                  <CardDescription>{editingCoupon ? `Editing ${editingCoupon}` : 'Add a new membership coupon code'}</CardDescription>
                </div>
                <Button variant="ghost" onClick={() => { setShowCouponForm(false); setEditingCoupon(null); }} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCouponSubmit} className="space-y-4">
                {!editingCoupon && (
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Coupon Code</label>
                  <Input
                    value={couponFormData.code}
                    onChange={(e) => setCouponFormData({...couponFormData, code: e.target.value.toUpperCase()})}
                    className="border-burgundy/30 focus:border-gold"
                    placeholder="e.g., LAUNCH2024, VIP50"
                    required
                  />
                </div>
                )}
                
                {editingCoupon && (
                <div className="bg-burgundy/5 p-3 rounded-lg">
                  <label className="block text-sm font-medium text-burgundy mb-1">Coupon Code</label>
                  <p className="text-lg font-mono font-bold text-burgundy">{editingCoupon}</p>
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Coupon Type</label>
                  <select
                    value={couponFormData.type}
                    onChange={(e) => setCouponFormData({...couponFormData, type: e.target.value})}
                    className="w-full p-2 border border-burgundy/30 rounded-md focus:border-gold"
                  >
                    <option value="forever_free">Forever Free (Permanent free access)</option>
                    <option value="referral_discount">Referral Discount (One-time)</option>
                    <option value="percentage">Percentage Discount</option>
                  </select>
                </div>

                {couponFormData.type === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Discount Duration</label>
                  <select
                    value={couponFormData.duration}
                    onChange={(e) => setCouponFormData({...couponFormData, duration: e.target.value})}
                    className="w-full p-2 border border-burgundy/30 rounded-md focus:border-gold"
                    data-testid="coupon-duration"
                  >
                    <option value="once">First payment only</option>
                    <option value="repeating">For a set number of months</option>
                    <option value="forever">Every payment until they cancel</option>
                  </select>
                  <p className="text-xs text-burgundy/60 mt-1">
                    {couponFormData.duration === 'once' && 'Discount applies to the first billing cycle only, then full price.'}
                    {couponFormData.duration === 'repeating' && 'Discount applies for a specific number of months, then full price.'}
                    {couponFormData.duration === 'forever' && 'Discount applies to every payment for the life of the subscription.'}
                  </p>
                </div>
                )}

                {couponFormData.type === 'percentage' && couponFormData.duration === 'repeating' && (
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Number of Months</label>
                  <Input
                    type="number"
                    value={couponFormData.duration_in_months}
                    onChange={(e) => setCouponFormData({...couponFormData, duration_in_months: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    placeholder="e.g., 3"
                    min="1"
                    max="36"
                    data-testid="coupon-duration-months"
                  />
                  <p className="text-xs text-burgundy/60 mt-1">How many months the discount lasts before switching to full price</p>
                </div>
                )}

                {couponFormData.type !== 'forever_free' && (
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-2">
                      Discount {couponFormData.type === 'percentage' ? 'Percentage' : 'Value'}
                    </label>
                    <Input
                      type="number"
                      value={couponFormData.value}
                      onChange={(e) => setCouponFormData({...couponFormData, value: e.target.value})}
                      className="border-burgundy/30 focus:border-gold"
                      placeholder="10"
                      min="0"
                      max={couponFormData.type === 'percentage' ? '100' : undefined}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Total Usage Limit (optional)</label>
                  <Input
                    type="number"
                    value={couponFormData.usage_limit || ''}
                    onChange={(e) => setCouponFormData({...couponFormData, usage_limit: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    placeholder="e.g., 48"
                    min="1"
                  />
                  <p className="text-xs text-burgundy/60 mt-1">Maximum total number of times this coupon can be used (leave blank for unlimited)</p>
                </div>

                {editingCoupon && (
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Times Used (current count)</label>
                  <Input
                    type="number"
                    value={couponFormData.times_used ?? 0}
                    onChange={(e) => setCouponFormData({...couponFormData, times_used: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    min="0"
                    data-testid="edit-times-used"
                  />
                  <p className="text-xs text-burgundy/60 mt-1">Reset this to 0 to clear test usage and free up slots for real users</p>
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Per User Limit (optional)</label>
                  <Input
                    type="number"
                    value={couponFormData.per_user_limit || ''}
                    onChange={(e) => setCouponFormData({...couponFormData, per_user_limit: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    placeholder="1"
                    min="1"
                  />
                  <p className="text-xs text-burgundy/60 mt-1">How many times each user can use this coupon</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Applies To Plans</label>
                  <div className="space-y-2 p-3 border border-burgundy/30 rounded-md">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!couponFormData.applies_to_plans || couponFormData.applies_to_plans.includes('monthly')}
                        onChange={(e) => {
                          const current = couponFormData.applies_to_plans || [];
                          if (e.target.checked) {
                            setCouponFormData({
                              ...couponFormData, 
                              applies_to_plans: [...current.filter(p => p !== 'monthly'), 'monthly']
                            });
                          } else {
                            setCouponFormData({
                              ...couponFormData, 
                              applies_to_plans: current.filter(p => p !== 'monthly')
                            });
                          }
                        }}
                        className="rounded border-burgundy/30"
                      />
                      <span className="text-sm text-burgundy">Monthly Membership ($20/month)</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!couponFormData.applies_to_plans || couponFormData.applies_to_plans.includes('yearly')}
                        onChange={(e) => {
                          const current = couponFormData.applies_to_plans || [];
                          if (e.target.checked) {
                            setCouponFormData({
                              ...couponFormData, 
                              applies_to_plans: [...current.filter(p => p !== 'yearly'), 'yearly']
                            });
                          } else {
                            setCouponFormData({
                              ...couponFormData, 
                              applies_to_plans: current.filter(p => p !== 'yearly')
                            });
                          }
                        }}
                        className="rounded border-burgundy/30"
                      />
                      <span className="text-sm text-burgundy">Yearly Membership ($192/year)</span>
                    </label>
                  </div>
                  <p className="text-xs text-burgundy/60 mt-1">
                    {(!couponFormData.applies_to_plans || couponFormData.applies_to_plans.length === 0) 
                      ? 'Leave both unchecked to apply to all plans' 
                      : 'Select which membership plans this coupon can be used with'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Expiration Date {couponFormData.type === 'forever_free' ? '(optional - leave blank for no expiration)' : '(optional)'}
                  </label>
                  <Input
                    type="date"
                    value={couponFormData.expires_at || ''}
                    onChange={(e) => setCouponFormData({...couponFormData, expires_at: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-burgundy/60 mt-1">
                    {couponFormData.type === 'forever_free' 
                      ? 'ForeverFree coupons can last forever or have an expiration'
                      : 'When this coupon expires (e.g., GIFT10 expires after 1 year)'}
                  </p>
                </div>

                <Button type="submit" className="w-full bg-burgundy hover:bg-burgundy/80 text-white" data-testid="coupon-submit-btn">
                  {editingCoupon ? 'Save Changes' : 'Create Coupon'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Product Types and Benefits Manager Component

export default CouponsTab;
